import type { JSX } from 'react';
import type { QontoClientResource } from '@voxler/contracts';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { formatAddress } from './billing-fields';
import { internalFetch } from '../lib/api';
import { Callout } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { SearchInput } from '@/components/ui/field';

/** Delay before searching Qonto, so that typing a name does not fire a request per key. */
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Search the clients of Qonto by name or SIREN, to link one (D-QONTO.8). A
 * record Qonto cannot invoice (no SIREN or e-mail) or already linked to
 * another client cannot be picked.
 */
export function QontoClientSearch({
  onSelect,
  currentClientId = null,
}: {
  onSelect: (record: QontoClientResource) => void;
  /** The client being linked: its own record stays selectable. */
  currentClientId?: string | null;
}): JSX.Element {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebounced(query.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [query]);

  const search = useQuery({
    queryKey: ['qonto-clients', debounced],
    queryFn: () =>
      internalFetch<{ clients: QontoClientResource[] }>(
        `/internal/qonto/clients?q=${encodeURIComponent(debounced)}`,
      ),
    enabled: debounced.length >= 2,
  });
  const results = search.data?.clients ?? [];

  return (
    <div className="grid gap-2">
      <SearchInput
        label="Rechercher un client Qonto"
        placeholder="Nom ou SIREN"
        wrapperClassName="w-full"
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
        }}
      />
      {search.isError ? (
        <Callout variant="destructive">La recherche dans Qonto a échoué.</Callout>
      ) : null}
      {debounced.length >= 2 && search.isSuccess && results.length === 0 ? (
        <p className="m-0 text-caption text-muted-foreground">Aucun client Qonto ne correspond.</p>
      ) : null}
      {results.length > 0 ? (
        <ul className="m-0 grid list-none gap-1.5 p-0">
          {results.map((record) => {
            const linkedElsewhere =
              record.linkedClientId !== null && record.linkedClientId !== currentClientId;
            const selectable = record.missing.length === 0 && !linkedElsewhere;
            return (
              <li
                key={record.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border px-3 py-2"
              >
                <div className="min-w-0">
                  <div className="text-[13px] font-medium">
                    {record.legalName ?? 'Sans nom'}
                    {record.siren !== null ? (
                      <span className="ml-2 font-mono text-muted-foreground">{record.siren}</span>
                    ) : null}
                  </div>
                  <div className="text-caption text-muted-foreground">
                    {record.email ?? 'Sans email'} · {formatAddress(record.billingAddress)}
                  </div>
                  {record.missing.length > 0 ? (
                    <div className="text-caption text-warning-fg">
                      {`${record.missing.join(', ')} manquant(s) : compléter la fiche dans Qonto.`}
                    </div>
                  ) : null}
                </div>
                {linkedElsewhere ? (
                  <Badge variant="neutral">Lié</Badge>
                ) : record.missing.length > 0 ? (
                  <Badge variant="warning">Incomplet</Badge>
                ) : null}
                {selectable ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      onSelect(record);
                    }}
                  >
                    Choisir
                  </Button>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
